import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { CORE, getToken } from "../../../lib/api";
import { useDemoMode } from "../../../lib/demo-mode";
import { streamSse } from "../../../lib/sse";

export interface DemoCallTurn {
  seq: number;
  speaker: "ai" | "customer" | "supervisor";
  text: string;
  translation?: string;
  flags?: string[];
  ts?: string;
  ts_ms?: number;
}

export interface DemoRiskEvent {
  id: string;
  call_id: string;
  rule_id: string;
  rule_label: string;
  tier: "green" | "amber" | "red";
  score_delta: number;
  evidence_turn_seq: number;
  ts: string;
  auto_resolved?: boolean;
  shadow?: boolean;
  false_positive?: boolean;
}

export interface DemoCallDoc {
  id: string;
  call_id: string;
  campaign_id?: string;
  campaign_name?: string;
  agent_id?: string;
  customer_ref?: string;
  customer_name?: string;
  language?: string;
  direction?: string;
  started_at: string;
  ended_at?: string | null;
  duration_sec?: number;
  state: "dialing" | "ringing" | "live" | "supervisor_listening" | "supervisor_steering" | "human_controlled" | "wrapping" | "ended";
  risk_tier: "green" | "amber" | "red";
  risk_score: number;
  risk_reasons?: string[];
  rules_fired?: string[];
  intervened?: boolean;
  disposition?: string | null;
  intent?: string;
  sentiment_trend?: number[];
  turns?: DemoCallTurn[];
  events?: DemoRiskEvent[];
  interventions?: any[];
  context?: any;
}

export interface HeadroomData {
  live_calls: number;
  safe_capacity: number;
  effective_capacity: number;
  headroom_pct: number;
  throttled: boolean;
  level: "green" | "amber" | "red";
  target_occupancy: number;
  active_supervisors: number;
  escalation_rate_per_min: number;
}

export interface SimStatusData {
  running: boolean;
  speed: number;
  amber_rate: number;
  red_rate: number;
  call_count: number;
  target_calls: number;
}

interface DemoLiveContextValue {
  calls: Record<string, DemoCallDoc>;
  headroom: HeadroomData | null;
  simStatus: SimStatusData | null;
  tick: number;
  rules: Record<string, any>;
  intervene: (callId: string, type: string, text?: string, ruleId?: string) => Promise<any>;
  startSim: () => Promise<void>;
  stopSim: () => Promise<void>;
  configSim: (cfg: { speed?: number; amber_rate?: number; red_rate?: number }) => Promise<void>;
  spawnScenario: (scenario: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const DemoLiveContext = createContext<DemoLiveContextValue | null>(null);

function beep() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.frequency.value = 880;
    o.type = "sine";
    g.gain.setValueAtTime(0.07, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
    o.start();
    o.stop(ctx.currentTime + 0.2);
    setTimeout(() => {
      try { ctx.close(); } catch {}
    }, 400);
  } catch {}
}

export function DemoLiveProvider({ children }: { children: ReactNode }) {
  const isDemo = useDemoMode();
  const [calls, setCalls] = useState<Record<string, DemoCallDoc>>({});
  const [headroom, setHeadroom] = useState<HeadroomData | null>(null);
  const [simStatus, setSimStatus] = useState<SimStatusData | null>(null);
  const [rules, setRules] = useState<Record<string, any>>({});
  const [tick, setTick] = useState(0);
  const prevTiers = useRef<Record<string, string>>({});

  const authHeaders = (): HeadersInit => {
    const token = getToken();
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  };

  const handleMessage = (msg: any) => {
    if (!msg || !msg.type) return;

    if (msg.type === "call_started" && msg.call) {
      const c = msg.call;
      const cid = c.id || c.call_id;
      prevTiers.current[cid] = c.risk_tier;
      setCalls((prev) => ({
        ...prev,
        [cid]: {
          ...c,
          id: cid,
          call_id: cid,
          turns: c.turns || [],
          events: c.events || [],
          interventions: c.interventions || [],
        },
      }));
    } else if (msg.type === "call_updated" && msg.call_id) {
      setCalls((prev) => {
        const c = prev[msg.call_id];
        if (!c) return prev;
        if (msg.risk_tier === "red" && prevTiers.current[msg.call_id] !== "red") {
          beep();
        }
        prevTiers.current[msg.call_id] = msg.risk_tier;
        return {
          ...prev,
          [msg.call_id]: {
            ...c,
            state: msg.state ?? c.state,
            risk_tier: msg.risk_tier ?? c.risk_tier,
            risk_score: msg.risk_score ?? c.risk_score,
            risk_reasons: msg.risk_reasons ?? c.risk_reasons,
            sentiment_trend: msg.sentiment_trend ?? c.sentiment_trend,
          },
        };
      });
    } else if (msg.type === "transcript_turn" && msg.call_id && msg.turn) {
      setCalls((prev) => {
        const c = prev[msg.call_id];
        if (!c) return prev;
        const turns = [...(c.turns || []), msg.turn].slice(-100);
        return {
          ...prev,
          [msg.call_id]: { ...c, turns },
        };
      });
    } else if (msg.type === "risk_event" && msg.call_id && msg.event) {
      setCalls((prev) => {
        const c = prev[msg.call_id];
        if (!c) return prev;
        const events = [...(c.events || []), msg.event].slice(-50);
        return {
          ...prev,
          [msg.call_id]: { ...c, events },
        };
      });
    } else if (msg.type === "call_ended" && msg.call_id) {
      setCalls((prev) => {
        const next = { ...prev };
        delete next[msg.call_id];
        return next;
      });
    } else if (msg.type === "headroom_update") {
      setHeadroom(msg);
    } else if (msg.type === "sim_status") {
      setSimStatus(msg);
    }
  };

  const loadInitial = async () => {
    try {
      const res = await fetch(`${CORE}/api/v2/supervision/demo/calls`, {
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const map: Record<string, DemoCallDoc> = {};
        for (const c of data.calls || []) {
          const cid = c.id || c.call_id;
          map[cid] = {
            ...c,
            id: cid,
            call_id: cid,
            turns: c.turns || [],
            events: c.events || [],
            interventions: c.interventions || [],
          };
          prevTiers.current[cid] = c.risk_tier;
        }
        setCalls(map);
        if (data.headroom) setHeadroom(data.headroom);
        if (data.sim_status) setSimStatus(data.sim_status);
        if (data.rules) {
          const rmap: Record<string, any> = {};
          data.rules.forEach((r: any) => { rmap[r.id] = r; });
          setRules(rmap);
        }
      }
    } catch {}
  };

  useEffect(() => {
    if (!isDemo) return;

    let aborted = false;
    let handle: { close: () => void } | null = null;
    let retryTimeout: any = null;

    void loadInitial();

    const connectSSE = () => {
      if (aborted) return;
      const token = getToken();
      const url = `${CORE}/api/v2/supervision/demo/stream${token ? `?token=${encodeURIComponent(token)}` : ""}`;

      handle = streamSse(url, undefined, (event, data: any) => {
        if (event === "snapshot") {
          const map: Record<string, DemoCallDoc> = {};
          for (const c of data.calls || []) {
            const cid = c.id || c.call_id;
            map[cid] = {
              ...c,
              id: cid,
              call_id: cid,
              turns: c.turns || [],
              events: c.events || [],
              interventions: c.interventions || [],
            };
          }
          setCalls(map);
          if (data.headroom) setHeadroom(data.headroom);
          if (data.sim_status) setSimStatus(data.sim_status);
        } else if (event === "error") {
          if (!aborted) {
            retryTimeout = setTimeout(connectSSE, 3000);
          }
        } else {
          handleMessage({ type: event, ...data });
        }
      });
    };

    connectSSE();

    const timer = setInterval(() => setTick((t) => t + 1), 1000);

    return () => {
      aborted = true;
      if (handle) handle.close();
      if (retryTimeout) clearTimeout(retryTimeout);
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo]);

  const intervene = async (callId: string, type: string, text?: string, ruleId?: string) => {
    const res = await fetch(`${CORE}/api/v2/supervision/demo/intervene`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ call_id: callId, type, text, rule_id: ruleId }),
    });
    return res.json();
  };

  const startSim = async () => {
    await fetch(`${CORE}/api/v2/supervision/demo/start`, {
      method: "POST",
      headers: authHeaders(),
    });
  };

  const stopSim = async () => {
    await fetch(`${CORE}/api/v2/supervision/demo/stop`, {
      method: "POST",
      headers: authHeaders(),
    });
  };

  const configSim = async (cfg: { speed?: number; amber_rate?: number; red_rate?: number }) => {
    await fetch(`${CORE}/api/v2/supervision/demo/config`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(cfg),
    });
  };

  const spawnScenario = async (scenario: string) => {
    await fetch(`${CORE}/api/v2/supervision/demo/scenario/${encodeURIComponent(scenario)}`, {
      method: "POST",
      headers: authHeaders(),
    });
  };

  return (
    <DemoLiveContext.Provider
      value={{
        calls,
        headroom,
        simStatus,
        tick,
        rules,
        intervene,
        startSim,
        stopSim,
        configSim,
        spawnScenario,
        refresh: loadInitial,
      }}
    >
      {children}
    </DemoLiveContext.Provider>
  );
}

export function useDemoLive() {
  const ctx = useContext(DemoLiveContext);
  if (!ctx) {
    throw new Error("useDemoLive must be used within DemoLiveProvider");
  }
  return ctx;
}
